<template>
  <div class="flex flex-col gap-5" :class="bare ? '' : 'rounded-lg bg-gray-900 p-6'">
    <template v-if="lead">
      <div class="flex flex-col gap-1">
        <h3 class="text-xs uppercase tracking-[1.5px] text-text-secondary">
          {{ isState ? $t('coach.lead_number.state_title') : $t('coach.lead_number.title') }}
        </h3>
        <p class="text-text-primary text-base font-medium">{{ heading }}</p>
        <p class="text-text-secondary text-xs">{{ scope }}</p>
      </div>
      <div class="flex flex-col gap-3.5 flex-1">
        <div v-for="bar in bars" :key="bar.label" class="flex flex-col gap-1.5">
          <div class="flex justify-between text-[13px]" :class="bar.tone === 'neutral' ? 'text-text-secondary' : 'text-text-primary'">
            <span>{{ bar.label }}<span v-if="bar.count" class="text-text-secondary"> · {{ bar.count }}</span></span>
            <span :class="TEXT[bar.tone]">{{ bar.value }}</span>
          </div>
          <div class="h-2 rounded bg-gray-800">
            <div class="h-2 rounded" :class="FILL[bar.tone]" :style="{ width: bar.width }" />
          </div>
        </div>
      </div>
      <p class="text-text-secondary text-xs">{{ caption }}</p>
    </template>
    <div v-else class="flex flex-col gap-3 animate-pulse" aria-hidden="true">
      <div class="h-3 w-1/2 rounded bg-gray-800" />
      <div class="h-4 w-3/4 rounded bg-gray-800" />
      <div class="h-2 w-full rounded bg-gray-800 mt-4" />
      <div class="h-2 w-2/3 rounded bg-gray-800" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MAP_NAMES } from '../../../composables/useCoachData';

const props = defineProps({
  // kind 'verdict' (or no kind): { metric, side, map_name, player, baseline, direction }
  // kind 'state': { side, map_name, allies, enemies, hold, if_die }, population numbers
  // null: skeleton
  lead: { type: Object, default: null },
  // null: one match, so no period in the scope line
  periodDays: { type: Number, default: null },
  mapLabel: { type: String, default: '' },
  // Inside another card: no background of its own (no box in a box)
  bare: { type: Boolean, default: false },
});

const { t, te, locale } = useI18n();

const TEXT = { bad: 'text-danger-400 font-medium', good: 'text-success-400 font-medium', neutral: '' };
const FILL = { bad: 'bg-danger-400', good: 'bg-success-500', neutral: 'bg-gray-500' };

const pct = (v) => `${(v * 100).toFixed(1)}%`;
// Same names as the page's map filter ("Dust II", not "Dust2").
const mapName = (name) => (name
  ? MAP_NAMES[name] || name.replace(/^de_/, '').replace(/^\w/, (c) => c.toUpperCase())
  : null);

// A metric impact-score-model ranks before this list learns it still gets the
// box, under a generic heading, rather than hiding it.
const metricLabel = (metric) => {
  const key = `coach.lead_number.metrics.${metric}`;
  // English is the fallback locale: an untranslated label still reads, in English.
  return te(key) || te(key, 'en') ? t(key) : t('coach.lead_number.unknown_metric');
};

// A state lead is how most players do in a spot, never the viewer's own stat,
// so it gets its own title, labels and caption.
const isState = computed(() => props.lead?.kind === 'state');

const heading = computed(() => (isState.value
  ? t('coach.lead_number.state_heading', { allies: props.lead.allies, enemies: props.lead.enemies })
  : metricLabel(props.lead.metric)));

const scope = computed(() => [
  props.lead?.side && t(`coach.lead_number.side_${props.lead.side}`),
  mapName(props.lead?.map_name) || props.mapLabel,
  !isState.value && props.periodDays && t('coach.profile_feedback_card.period_days', { days: props.periodDays }),
].filter(Boolean).join(' · '));

// What each stat's sample counts, from the denominators impact-score-model's
// sentences use. A metric missing here shows no count rather than a wrong unit.
const UNITS = {
  blind_death_rate: 'deaths',
  blindside_death_rate: 'deaths',
  death_traded_rate: 'deaths',
  man_up_death_rate: 'deaths',
  moving_death_rate: 'deaths',
  opening_death_share: 'deaths',
  util_hoard_death_rate: 'deaths',
  man_down_fight_rate: 'duels',
  stacked_zone_fight_rate: 'duels',
  flash_blind_rate: 'flashes',
  team_flash_rate: 'flashes',
  he_effect_rate: 'he_grenades',
  early_util_dump_rate: 'grenades',
  force_round_rate: 'rounds',
  no_kit_fullbuy_rate: 'rounds',
  no_util_buy_rate: 'rounds',
  off_meta_buy_rate: 'rounds',
  underbuy_rate: 'rounds',
};
// The player's own count is exact; a population count is rounded up to the
// nearest thousand ("~13,000"), since its precision says nothing to a player.
const sample = (n, unit, rough) => {
  if (!unit || !Number.isFinite(n) || n <= 0) return null;
  const shown = rough && n >= 1000 ? Math.ceil(n / 1000) * 1000 : n;
  const count = `${rough && n >= 1000 ? '~' : ''}${shown.toLocaleString(locale.value)}`;
  return t(`coach.lead_number.counts.${unit}`, { n: count }, shown);
};

// Scaled so the larger bar fills 80% of the track.
const bars = computed(() => {
  const l = props.lead || {};
  const rows = isState.value
    ? [
      {
        label: t('coach.lead_number.hold', { allies: l.allies, enemies: l.enemies }),
        v: l.hold,
        tone: 'good',
        count: sample(l.hold_n, 'rounds', true),
      },
      {
        label: t('coach.lead_number.if_you_die', { allies: l.allies - 1, enemies: l.enemies }),
        v: l.if_die,
        tone: 'bad',
        count: sample(l.if_die_n, 'rounds', true),
      },
    ]
    : [
      {
        label: t('coach.lead_number.you'), v: l.player, tone: 'bad', count: sample(l.n, UNITS[l.metric], false),
      },
      {
        label: t('coach.lead_number.your_level'),
        v: l.baseline,
        tone: 'neutral',
        count: sample(l.baseline_n, UNITS[l.metric], true),
      },
    ];
  const max = Math.max(...rows.map((r) => r.v || 0)) || 1;
  return rows.map((r) => ({ ...r, value: pct(r.v || 0), width: `${Math.round(((r.v || 0) / max) * 80)}%` }));
});

const caption = computed(() => {
  if (isState.value) return t('coach.lead_number.state_caption');
  return props.lead?.direction === 'less' ? t('coach.lead_number.higher_is_better') : t('coach.lead_number.lower_is_better');
});
</script>
